import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { getQuoteOrderValue, type QuoteListRow } from 'src/api/db/apiQuote'
import { Card } from 'src/shared/components/ui/card'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Box, Building, Calendar, Hash } from 'lucide-react'
import { paths } from 'src/routes/paths'
import { formatDate as formatDateUtil } from 'src/shared/utils'
import { getQuotePreviewThumbnails } from 'src/shared/utils/quoteItemThumbnail'
import {
  DELIVERED_STATUS,
  getQuoteStatusDisplayLabel,
  getQuoteStatusOption,
} from 'src/shared/consts/quoteStatus'
import { ReorderButton } from './ReorderButton'
import { Badge } from 'src/shared/components/ui/badge'
import { Image } from 'src/shared/components/ui/image'
import { cn } from 'src/shared/components/ui/utils'
import { QuoteOrderValue } from 'src/shared/components/QuoteOrderValue'

export const RequestCard = ({
  quote,
  listSearch = '',
  detailPath = paths.requests,
  basePath = paths.myAccount,
  showOrg = false,
}: {
  quote: QuoteListRow
  /** Preserved list query, e.g. `?segment=delivered`, when opening detail */
  listSearch?: string
  detailPath?: string
  /** App or admin base, e.g. `/app` or `/admin` */
  basePath?: string
  showOrg?: boolean
}) => {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const isAdmin = useIsAdmin()
  const showOrganization = showOrg || isAdmin

  const { previews, extraCount } = useMemo(
    () => getQuotePreviewThumbnails(quote.quote_item, 4),
    [quote.quote_item]
  )

  const visiblePreviews = previews.filter((preview) => preview.url)
  const hasProofs = (quote.quote_proof?.length ?? 0) > 0
  const statusLabel = getQuoteStatusDisplayLabel(quote.status, { hasProofs })

  const requestHref =
    `${basePath}${detailPath}/${quote.public_id}` +
    (listSearch.startsWith('?') ? listSearch : listSearch ? `?${listSearch}` : '')

  const orderLabel = quote.name?.trim() || quote.public_id || ''
  const orderValue = getQuoteOrderValue(quote)
  const showReorder = !isAdmin && quote.status === DELIVERED_STATUS && !!orderLabel
  const orderedOn = quote.created_at?.slice(0, 10)
  const reorderMessage = orderedOn
    ? t(
        `Reorder all products from ${orderLabel}, ordered ${orderedOn}`,
        `Beställ om alla produkter från ${orderLabel}, beställd ${orderedOn}`
      )
    : t(
        `Reorder all products from ${orderLabel}`,
        `Beställ om alla produkter från ${orderLabel}`
      )

  return (
    <Card
      wrapperClassName="h-full"
      className="h-full cursor-pointer p-5 transition-shadow duration-200 hover:shadow-soft"
      onClick={() => navigate(requestHref)}
    >
      <Column className="h-full gap-4">
        <Row className="items-start justify-between gap-3">
          <p className="line-clamp-2 text-base text-foreground">{quote.name}</p>
          <Badge
            size="md"
            variant={getQuoteStatusOption(quote.status)?.color}
            className="shrink-0"
          >
            {t(statusLabel)}
          </Badge>
        </Row>

        <QuoteOrderValue amount={orderValue} showLabel />

        {visiblePreviews.length > 0 && (
          <Row className="items-center">
            {visiblePreviews.map(({ key, url }, index) => (
              <div
                key={key}
                className={cn(
                  'h-9 w-9 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-muted shadow-soft-sm',
                  index > 0 && '-ml-2'
                )}
              >
                <Image
                  src={url!}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
            {extraCount > 0 && (
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-muted text-[0.625rem] font-medium tabular-nums text-muted-foreground shadow-soft-sm',
                  visiblePreviews.length > 0 && '-ml-2'
                )}
              >
                +{extraCount}
              </div>
            )}
          </Row>
        )}

        <Column className="mt-auto gap-4">
          <Column className="gap-2.5">
            <Row className="items-center gap-2">
              <Box className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {quote.quote_item?.length} {t('items', 'artiklar')}
              </span>
            </Row>

            <Row className="items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {formatDateUtil(quote.created_at, locale)}
              </span>
            </Row>

            <Row className="items-center gap-2">
              <Hash className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm text-muted-foreground">
                {quote.public_id}
              </span>
            </Row>

            {showOrganization && (
              <Row className="items-center gap-2">
                <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm text-muted-foreground">
                  {quote.organization?.name || '-'}
                </span>
              </Row>
            )}
          </Column>

          {showReorder && (
            <ReorderButton
              message={reorderMessage}
              requestName={`${t('Reorder', 'Ombeställning')}: ${orderLabel}`}
            />
          )}
        </Column>
      </Column>
    </Card>
  )
}

/** @deprecated Use RequestCard */
export const OrderCard = RequestCard
